import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, input, output } from '@angular/core';

declare const bootstrap: {
  Modal: new (el: Element, options?: { backdrop?: boolean | 'static'; keyboard?: boolean }) => {
    show(): void;
    hide(): void;
  };
};

/**
 * Generic Bootstrap modal chrome — title, close button, body via content projection.
 * Use `show()` / `hide()` on the template ref to control visibility.
 *
 * Example:
 *   <mac-modal #myModal title="Add Person" icon="person-plus" size="lg">
 *     <div class="modal-body">…form…</div>
 *     <div class="modal-footer">
 *       <button data-bs-dismiss="modal">Cancel</button>
 *       <button (click)="submit()">Save</button>
 *     </div>
 *   </mac-modal>
 *   <button (click)="myModal.show()">Open</button>
 */
@Component({
  selector: 'mac-modal',
  host: { '[attr.title]': 'null' },
  template: `
    <div #el class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" [class]="dialogClass()">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              @if (icon()) {
                <i class="bi me-2" [class]="'bi-' + icon()"></i>
              }
              {{ title() }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  `,
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  readonly title = input.required<string>();
  readonly icon = input<string | null>(null);
  readonly size = input<'sm' | 'md' | 'lg' | 'xl'>('md');
  readonly closed = output<void>();

  @ViewChild('el') private el?: ElementRef<HTMLDivElement>;
  private modal?: { show(): void; hide(): void };
  private readonly onHidden = (): void => this.closed.emit();

  ngAfterViewInit(): void {
    this.el?.nativeElement.addEventListener('hidden.bs.modal', this.onHidden);
  }

  ngOnDestroy(): void {
    this.el?.nativeElement.removeEventListener('hidden.bs.modal', this.onHidden);
  }

  protected dialogClass(): string {
    switch (this.size()) {
      case 'sm': return 'modal-dialog modal-dialog-centered modal-sm';
      case 'lg': return 'modal-dialog modal-dialog-centered modal-lg';
      case 'xl': return 'modal-dialog modal-dialog-centered modal-xl';
      default:   return 'modal-dialog modal-dialog-centered';
    }
  }

  show(): void {
    if (!this.el) return;
    if (!this.modal) {
      // App-wide policy: clicking the backdrop or pressing Escape must NOT
      // dismiss the modal. Only Cancel / X close it.
      this.modal = new bootstrap.Modal(this.el.nativeElement, {
        backdrop: 'static',
        keyboard: false,
      });
    }
    this.modal.show();
  }

  hide(): void {
    this.modal?.hide();
  }

  /**
   * Hide this modal, then run `after` once it has fully closed.
   *
   * Use this to make one modal *replace* another instead of stacking on it.
   * Bootstrap animates the hide and only tears its backdrop down at the end, so
   * calling `show()` on a second modal straight after `hide()` leaves a
   * stranded backdrop and a stuck `.modal-open` on <body> — the two dialogs end
   * up layered with two sets of buttons. Sequencing on `hidden.bs.modal` avoids
   * that entirely.
   *
   * Falls through immediately when this modal isn't actually open, since
   * `hidden.bs.modal` would never fire and `after` would be lost.
   */
  hideThen(after: () => void): void {
    const el = this.el?.nativeElement;
    if (!el || !this.modal || !el.classList.contains('show')) {
      after();
      return;
    }
    const once = (): void => {
      el.removeEventListener('hidden.bs.modal', once);
      after();
    };
    el.addEventListener('hidden.bs.modal', once);
    this.modal.hide();
  }
}
